import React, { useEffect, useState } from "react";
import "../../InvoiceModal.css";
import { fixedDecimals } from "../../../../helpers/helpers";
import { useTranslation } from "react-i18next";

const CarrierBill = ({ data }) => {
  const { t } = useTranslation("finances");
  const hasInsurance = !!data?.insurance;
  const hasShipmentCharges = !!data?.billingAutomation;

  const [taxCharges, setTaxCharges] = useState([]);
  const [additionalCharges, setAdditionalCharges] = useState([]);

  useEffect(() => {
    const getUniqueCharges = (type) =>
      data?.billingAutomation
        ?.flatMap((item) => item?.[type] || [])
        ?.filter(
          (charge, index, self) =>
            charge &&
            index === self.findIndex((c) => c?.NameEn === charge?.NameEn)
        );

    setTaxCharges(getUniqueCharges("tax_charges"));
    setAdditionalCharges(getUniqueCharges("additional_charges"));
  }, [data]);

  const findChargeByNameEn = (nameEn, index, type) => {
    const charges = data?.billingAutomation[index]?.[type];
    return charges?.find((charge) => charge?.NameEn === nameEn) || false;
  };

  const findTaxChargeByNameEn = (nameEn, index) =>
    findChargeByNameEn(nameEn, index, "tax_charges");

  const findAdditionalChargeByNameEn = (nameEn, index) =>
    findChargeByNameEn(nameEn, index, "additional_charges");

  const calculateTotal = () => {
    const totalRow = {
      base_price: 0,
      fuel_charge: 0,
      freight_charge: 0,
      signature_charge: 0,
      residential_charge: 0,
      PickupCharge: 0,
      insurance_charge: 0,
      sub_total: 0,
      total: 0,
      taxTotals: {},
      additionalTotals: {},
    };

    if (data?.billingAutomation?.length <= 1) return null;

    data?.billingAutomation?.forEach((item) => {
      [
        "base_price",
        "fuel_charge",
        "freight_charge",
        "signature_charge",
        "residential_charge",
        "PickupCharge",
        "insurance_charge",
        "sub_total",
        "total",
      ].forEach((key) => {
        const value = Number(item?.[key]) || 0;
        totalRow[key] += value;
      });

      taxCharges?.forEach((tax) => {
        const found = item?.tax_charges?.find((t) => t?.NameEn === tax?.NameEn);
        const val = Number(found?.Charge) || 0;
        totalRow.taxTotals[tax.NameEn] =
          (totalRow.taxTotals[tax.NameEn] || 0) + val;
      });

      additionalCharges?.forEach((add) => {
        const found = item?.additional_charges?.find(
          (a) => a?.NameEn === add?.NameEn
        );
        const val = Number(found?.Charge) || 0;
        totalRow.additionalTotals[add.NameEn] =
          (totalRow.additionalTotals[add.NameEn] || 0) + val;
      });
    });

    return totalRow;
  };

  const totals = calculateTotal();

  return (
    <div className="invoice__billing_admin_wrap">
      <div className="invmodal__shipment_detailshding">
        {t("invoice.carrierBill.title")}{" "}
      </div>
      <small>
        {t("invoice.carrierBill.hint")}
      </small>

      <div className="overflow-x-auto">
        <table className="w-full min-w-max mb-2 text-[10px] md:text-xxs lg:text-xs xl:text-sm font-medium">
          <thead>
            <tr>
              <th className="p-1 text-center font-semibold">#</th>
              <th className="p-1 text-center font-semibold">
                {t("invoice.charges.base")}
              </th>
              <th className="p-1 text-center font-semibold">
                {t("invoice.charges.fuel")}
              </th>
              <th className="p-1 text-center font-semibold">
                {t("invoice.charges.freight")}
              </th>
              <th className="p-1 text-center font-semibold">
                {t("invoice.charges.signature")}
              </th>
              <th className="p-1 text-center font-semibold">
                {t("invoice.charges.residential")}
              </th>
              <th className="p-1 text-center font-semibold">
                {t("invoice.charges.pickup")}
              </th>
              {hasInsurance && (
                <th className="p-1 text-center font-semibold">
                  {t("invoice.charges.insurance")}
                </th>
              )}

              {/* Move additionalCharges BEFORE Sub Total */}
              {hasShipmentCharges &&
                additionalCharges?.map((charge, index) => (
                  <th className="p-1 text-center font-semibold" key={index}>
                    {charge?.NameEn}
                  </th>
                ))}

              <th className="p-1 text-center font-semibold">
                {t("invoice.charges.subTotal")}
              </th>

              {hasShipmentCharges &&
                taxCharges?.map((tax, index) => (
                  <th className="p-1 text-center font-semibold" key={index}>
                    {tax?.NameEn}
                  </th>
                ))}

              <th className="p-1 text-center font-semibold">
                {t("invoice.charges.total")}
              </th>
              <th className="p-1 text-center font-semibold">
                {t("invoice.charges.invoice")}
              </th>
            </tr>
          </thead>

          <tbody>
            {data?.billingAutomation?.map((charge, index) => (
              <tr key={index}>
                <td className="p-1 text-center">{index + 1}</td>

                {[
                  "base_price",
                  "fuel_charge",
                  "freight_charge",
                  "signature_charge",
                  "residential_charge",
                  "PickupCharge",
                  ...(hasInsurance ? ["insurance_charge"] : []),
                ].map((key) => (
                  <td key={key} className="p-1 text-center">
                    {charge?.[key] && charge?.[key] !== 0 ? charge?.[key] : "-"}
                  </td>
                ))}

                {/* Move additionalCharges BEFORE Sub Total */}
                {hasShipmentCharges &&
                  additionalCharges?.map((charge, idx) => (
                    <td className="p-1 text-center" key={idx}>
                      {findAdditionalChargeByNameEn(charge?.NameEn, index)
                        ?.Charge ?? "-"}
                    </td>
                  ))}

                <td className="p-1 text-center">
                  {charge?.sub_total && charge?.sub_total !== 0
                    ? charge?.sub_total
                    : "-"}
                </td>

                {hasShipmentCharges &&
                  taxCharges?.map((tax, idx) => (
                    <td className="p-1 text-center" key={idx}>
                      {findTaxChargeByNameEn(tax?.NameEn, index)?.Charge ?? "-"}
                    </td>
                  ))}

                <td className="p-1 text-center">
                  {charge?.total && charge?.total !== 0
                    ? fixedDecimals(charge?.total)
                    : "-"}
                </td>
                <td className="p-1 text-center">{charge?.invoice_id ?? "-"}</td>
              </tr>
            ))}

            {/* TOTAL ROW */}
            {totals && (
              <tr className="font-semibold bg-ui-surface-sunken">
                <td className="p-1 text-center">
                  {t("invoice.charges.total")}
                </td>
                {[
                  "base_price",
                  "fuel_charge",
                  "freight_charge",
                  "signature_charge",
                  "residential_charge",
                  "PickupCharge",
                  ...(hasInsurance ? ["insurance_charge"] : []),
                ].map((key) => (
                  <td key={key} className="p-1 text-center">
                    {totals[key] !== 0 ? fixedDecimals(totals[key]) : "0"}
                  </td>
                ))}

                {/* Move additionalCharges BEFORE Sub Total */}
                {hasShipmentCharges &&
                  additionalCharges?.map((charge, idx) => (
                    <td className="p-1 text-center" key={idx}>
                      {totals.additionalTotals?.[charge?.NameEn] !== 0
                        ? fixedDecimals(
                            totals?.additionalTotals?.[charge?.NameEn]
                          )
                        : "0"}
                    </td>
                  ))}

                <td className="p-1 text-center">
                  {totals.sub_total !== 0
                    ? fixedDecimals(totals?.sub_total)
                    : "0"}
                </td>

                {hasShipmentCharges &&
                  taxCharges?.map((tax, idx) => (
                    <td className="p-1 text-center" key={idx}>
                      {totals.taxTotals?.[tax?.NameEn] !== 0
                        ? fixedDecimals(totals?.taxTotals?.[tax?.NameEn])
                        : "0"}
                    </td>
                  ))}

                <td className="p-1 text-center">
                  {totals.total !== 0 ? fixedDecimals(totals?.total) : "0"}
                </td>
                <td className="p-1 text-center">-</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CarrierBill;
